import { mapRdvToCommande } from '../../utils/equipeRdvRules';
import { getRdvPaymentView } from '../../utils/rdvPaymentDisplay';
import { resolveRdvPaymentTone, rdvPaymentToneClassName } from '../../utils/rdvPaymentTone';
import {
  clientDisplayName,
  formatParisClockColon,
  rdvDurationMinutes,
} from '../../utils/planningDayParis';
import { rdvHasClientParticularity } from '../../utils/rdvClientNote';

function mixWithWhite(hex, amount = 0.82) {
  const raw = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const n = parseInt(raw, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function cardSurface(rdv) {
  const statut = String(rdv?.statut || '').toUpperCase();
  if (statut === 'EN_ATTENTE' || statut === 'EN_ATTENTE_PAIEMENT') {
    return { backgroundColor: '#FFF6EC', borderColor: '#F3D7B5' };
  }
  if (statut === 'ABSENT') {
    return { backgroundColor: '#FDECEC', borderColor: '#F3C7C7' };
  }
  if (statut === 'TERMINE') {
    return { backgroundColor: '#F4F5F7', borderColor: '#E5E7EB' };
  }
  const wash = mixWithWhite(rdv?.prestation?.couleur, 0.78);
  return {
    backgroundColor: wash || '#EEFBF3',
    borderColor: wash ? 'rgba(15, 23, 42, 0.06)' : '#D7F0E2',
  };
}

function statusTone(statut) {
  if (statut === 'CONFIRME') return { label: 'Confirmé', className: 'text-emerald-700' };
  if (statut === 'EN_ATTENTE') return { label: 'En attente', className: 'text-amber-700' };
  if (statut === 'EN_ATTENTE_PAIEMENT') return { label: 'Paiement', className: 'text-orange-700' };
  if (statut === 'TERMINE') return { label: 'Terminé', className: 'text-gray-500' };
  if (statut === 'ABSENT') return { label: 'Absent', className: 'text-rose-600' };
  if (statut === 'ANNULE') return { label: 'Annulé', className: 'text-gray-400' };
  return { label: statut || '—', className: 'text-gray-500' };
}

export function paymentChip(rdv) {
  const commande = mapRdvToCommande(rdv);
  const view = getRdvPaymentView(commande);
  if (!view) return null;

  const tone = resolveRdvPaymentTone(commande);
  const className = rdvPaymentToneClassName('chip', tone);

  if (view.isRefunded) {
    return { label: 'Remboursé', className: rdvPaymentToneClassName('chip', 'neutral') };
  }
  if (view.amountPaid > 0 && view.amountDueLater <= 0.009) {
    return { label: view.summaryLabel || 'Payé', className };
  }
  if (view.modePaiement === 'surplace' && view.amountDueLater > 0.009 && view.amountPaid <= 0.009) {
    return { label: view.summaryLabel || 'Sur place', className };
  }
  if (view.amountDueLater > 0.009 && !(view.amountPaid >= view.total && view.total > 0)) {
    return { label: view.summaryLabel || 'À encaisser', className };
  }
  if (view.modePaiement === 'enligne' && view.amountPaid <= 0.009 && view.amountDueLater > 0.009) {
    return { label: view.summaryLabel || 'À encaisser', className };
  }
  return {
    label: view.summaryLabel || 'Paiement',
    className,
  };
}

function formatDuration(minutes) {
  if (!minutes) return '';
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? '1h' : `${h}h`;
  }
  if (minutes > 60 && minutes % 30 === 0) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
  }
  return `${minutes} min`;
}

/**
 * Carte RDV pastel — horaire, client, prestation, statut, paiement.
 */
export default function PlanningRdvCard({
  rdv,
  onClick,
  compact = false,
  showIntervenant = false,
  intervenantLabel = null,
  showParticularityIndicator = false,
}) {
  const statut = String(rdv?.statut || '').toUpperCase();
  const tone = statusTone(statut);
  const surface = cardSurface(rdv);
  const start = formatParisClockColon(rdv.dateDebut);
  const end = rdv.dateFin ? formatParisClockColon(rdv.dateFin) : null;
  const client = clientDisplayName(rdv.user);
  const prestation = rdv.prestation?.nom?.trim() || 'Prestation';
  const duration = rdvDurationMinutes(rdv);
  const pay = paymentChip(rdv);
  const hasParticularity = showParticularityIndicator && rdvHasClientParticularity(rdv);

  return (
    <button
      type="button"
      onClick={() => onClick?.(rdv)}
      style={surface}
      className="flex h-full w-full flex-col overflow-hidden rounded-xl border px-2.5 py-2 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition hover:brightness-[0.99]"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-[11px] font-semibold leading-4 tabular-nums text-gray-800">
          {start}
          {end ? <span className="font-medium text-gray-500"> – {end}</span> : null}
          {!compact ? (
            <span className="font-medium text-gray-400"> ({formatDuration(duration)})</span>
          ) : null}
        </p>
        <span className="flex shrink-0 items-center gap-1">
          {hasParticularity ? (
            <span
              className="inline-flex h-2 w-2 rounded-full bg-red-500 ring-2 ring-red-100"
              title="Particularité renseignée"
              aria-label="Particularité renseignée"
            />
          ) : null}
          <span className={`text-[10px] font-semibold leading-4 ${tone.className}`}>{tone.label}</span>
        </span>
      </div>
      <p className="mt-0.5 truncate text-[13px] font-semibold leading-4 text-gray-900">{client}</p>
      <p className="truncate text-[11px] leading-4 text-gray-600">{prestation}</p>
      {pay && !compact ? (
        <span className={`mt-1 self-start rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${pay.className}`}>
          {pay.label}
        </span>
      ) : null}
      {showIntervenant && intervenantLabel ? (
        <p className="mt-0.5 truncate text-[10px] text-gray-500">{intervenantLabel}</p>
      ) : null}
    </button>
  );
}
